import { useEffect, useRef } from 'react';

/**
 * Marginalia for the themes with no colour to carry them.
 *
 * «Paper & Marker» is ink on ruled paper, which is right for Years 5–6 and
 * exactly why it feels bare to a younger child. Rather than paint it — which
 * would break the one idea the theme has — a ruler, a compass, a pencil draw
 * themselves into the margins as the round goes on, in the same ink as the
 * numerals. The page fills up as the child works, which is the reward.
 *
 * Six drawings, one per correct answer, then it stops: a margin that keeps
 * filling forever becomes noise behind the board.
 */

interface Doodle { d: string; box: string; len: number }

/** `len` is the rough path length, used to animate the stroke drawing itself. */
const DOODLES: Doodle[] = [
  {
    // ruler
    d: 'M2 4h96v26H2Z M14 4v12 M26 4v8 M38 4v12 M50 4v8 M62 4v12 M74 4v8 M86 4v12',
    box: '0 0 100 34',
    len: 300,
  },
  {
    // compass
    d: 'M30 3a6 6 0 1 1 0 12 6 6 0 1 1 0-12 M28 15 10 66 M32 15l18 51 M14 54q16 8 32 0 M50 66l4 6',
    box: '0 0 60 74',
    len: 260,
  },
  {
    // pencil
    d: 'M8 13 22 5h46v16H22Z M22 5v16 M8 13l6-3 M8 13l6 3',
    box: '0 0 74 26',
    len: 200,
  },
  {
    // number line
    d: 'M4 16h102 M16 10v12 M41 10v12 M66 10v12 M91 10v12 M100 11l6 5-6 5',
    box: '0 0 110 26',
    len: 190,
  },
  {
    // apple
    d: 'M27 18C14 8 4 20 6 34c2 16 14 24 21 18 7 6 19-2 21-18 2-14-8-26-21-16Z M27 18c0-8 3-13 9-15',
    box: '0 0 54 60',
    len: 220,
  },
  {
    // star
    d: 'M27 3l7 17 18 1-14 12 5 17-16-10-16 10 5-17L2 21l18-1Z',
    box: '0 0 54 52',
    len: 190,
  },
];

/** Where each one sits. Logical properties, so RTL mirrors it for free. */
const SPOTS = [
  'inset-block-end:4px; inset-inline-start:6px; width:96px; height:32px; transform:rotate(-4deg)',
  'inset-block-start:4px; inset-inline-end:4px; width:44px; height:56px; transform:rotate(7deg)',
  'inset-block-start:8px; inset-inline-start:4px; width:66px; height:24px; transform:rotate(-11deg)',
  'inset-block-end:34%; inset-inline-start:0; width:92px; height:24px; transform:rotate(3deg)',
  'inset-block-end:8px; inset-inline-end:8px; width:36px; height:40px',
  'inset-block-start:42%; inset-inline-end:2px; width:32px; height:30px; transform:rotate(12deg)',
];

export function Doodles({ nonce, enabled }: { nonce: number; enabled: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  const drawn = useRef(0);
  const seen = useRef(0);

  // A new board starts a blank page.
  useEffect(() => {
    if (nonce !== 0) return;
    drawn.current = 0;
    seen.current = 0;
    if (host.current) host.current.replaceChildren();
  }, [nonce]);

  useEffect(() => {
    if (!enabled || nonce === 0 || nonce === seen.current) return;
    seen.current = nonce;
    const el = host.current;
    if (!el || drawn.current >= DOODLES.length) return;

    const doodle = DOODLES[drawn.current]!;
    const spot = SPOTS[drawn.current]!;
    drawn.current += 1;

    const wrap = document.createElement('div');
    wrap.className = 'doodle';
    wrap.setAttribute('style', spot);
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    wrap.innerHTML =
      `<svg viewBox="${doodle.box}" aria-hidden="true"><path d="${doodle.d}"` +
      (reduced ? '' : ` style="stroke-dasharray:${doodle.len};stroke-dashoffset:${doodle.len}"`) +
      ' /></svg>';
    if (!reduced) wrap.classList.add('is-drawing');
    el.appendChild(wrap);
  }, [nonce, enabled]);

  if (!enabled) return null;
  return <div className="doodle-layer" ref={host} aria-hidden="true" />;
}
